// TemplatesCarousel 中点击模板项打开视频弹窗
import React, { useMemo, useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Image } from "@/components/common/image";
import { TruncatedText } from "@/components/common/truncated-text";
import {
  ChevronRightThinIcon,
  ChevronLeftThinIcon,
  MoreIcon,
  TrashIcon,
} from "@/components/ui/icon";
import AgentTemplateVideoModal from "@/components/agent-modal-template-video";
import { TemplateEmptyIcon } from "../agent-dashboard-user-detail/assets";
import { AddTemplateIcon } from "../agent-dashboard-company-asset/assets";
import { cn } from "@/lib/utils";
import { VoiceTemplateItemData } from "@/services/user-audio-template-list";
import { useStyleEditorMicroApp } from "@/hooks/use-style-editor-microapp";
import { agentCompanyAssetHooks, agentTemplateHooks, iamAccountHooks } from "@/manager";
import { createPortal } from "react-dom";
import StyleEditorMicroApp from "@bhb-frontend/style-editor-microapp";
import { AgentUserActorData } from "@/manager/manager/agent-user-voice-list-manager";
import { DeleteConfirmModal, Tooltip } from "../ui/modal";
import { MoreActionItem, MoreActionPopover } from "../common/more-action-popover";
import { EditIcon } from "../agent-dashboard/assets";
import { LoadingOverlay } from "../common/loading-overlay";
import { useToast } from "@/hooks/use-toast";


export type TemplateItem = {
  id: number;
  name: string;
  coverUrl: string;
  videoUrl?: string;
  coverVideo?: string;
  uuid?: string;
};

export type TemplatesCarouselProps = {
  templates?: TemplateItem[];
  // 用户的资产数字人列表
  agentUserActors?: AgentUserActorData[];
  // 用户的资产声音列表
  voiceTemplates?: Audio[];
  showVideoAction?: boolean;
  loading?: boolean;
  agentUserLoading?: boolean;
  onOpenAll?: () => void;
  onCreateSuccess?: () => void;
  onDetailLoadingChange?: (loading: boolean) => void;
  onDeleteLoadingChange?: (loading: boolean) => void;
};

export interface TemplatesCarouselAction {
  onDelete: (uuid: string) => Promise<void>;
  onEdit: (uuid: string) => void;
}

export const TemplatesCarousel = forwardRef<TemplatesCarouselAction, TemplatesCarouselProps>(({
  templates,
  voiceTemplates,
  agentUserActors,
  showVideoAction,
  onOpenAll,
  loading,
  agentUserLoading,
  onCreateSuccess,
  onDetailLoadingChange,
}, ref) => {
  const { t } = useTranslation();
  const params = useParams();
  const toast = useToast();
  const items = useMemo(() => templates || [], [templates]);
  const pageSize = 7;
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const [page, setPage] = useState(0);

  const startXRef = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (startXRef.current == null) return;
    const diff = e.changedTouches[0].clientX - startXRef.current;
    if (Math.abs(diff) > 50) {
      if (diff < 0) setPage((p) => Math.min(totalPages - 1, p + 1));
      else setPage((p) => Math.max(0, p - 1));
    }
    startXRef.current = null;
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      if (e.deltaX > 0) setPage((p) => Math.min(totalPages - 1, p + 1));
      else setPage((p) => Math.max(0, p - 1));
    }
  };

  const pageItems = items.slice(page * pageSize, page * pageSize + pageSize);

  // 用分页位置判断左右是否可滚动
  const canScrollLeft = page > 0;
  const canScrollRight = page < totalPages - 1;

  // 模板相关
  const { loading: saveLoading, save } = agentTemplateHooks.useSaveAgentTemplate();
  const { loading: detailLoading, fetchDetail } = agentTemplateHooks.useTemplateDetail();
  const { loading: updateLoading, update } = agentTemplateHooks.useUpdateAgentTemplate();
  const { loading: deleteLoading, deleteTemplate } = agentTemplateHooks.useDeleteAgentTemplate();

  const { account } = iamAccountHooks.useData();
  const [triggerId, setTriggerId] = useState("");

  // 定义状态
  const [isPendingAdd, setIsPendingAdd] = useState(false);

  // 微应用 Hook
  const {
    env,
    initialAppLoading,
    enablePresetTemplates,
    handleMicroAppMessage,
    handleMounted,
    handleUnmounted,
    openEditor
  } = useStyleEditorMicroApp({
    account,
    openPresetTemplates: true,
    openPublicActors: true,
    openPublicVoices: true,
    actors: agentUserActors?.filter(item => item.generateStatus === 2).map(item => ({
      id: item.id,
      name: item.name,
      coverUrl: item.image,
      renderUrl: item.image,
      width: item.width,
      height: item.height,
      modelId: item.modelId,
    })),
    voices: voiceTemplates.map((item, index) => ({
      id: item.voiceTtsId,
      name: item.name,
      exampleUrl: item.audioUrl,
    })),
    onSaveSuccess: async ({ styleSaveParams, actorModelId }) => {
      if (!triggerId) {
        // 构建新建保存参数
        const templateName = enablePresetTemplates.find(item => item.templateId === styleSaveParams.selectedId)?.templateName || '';

        const createParams = {
          styleId: styleSaveParams.styleId,
          templateName,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          coverUrl: styleSaveParams.coverUrl,
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
          templateId: styleSaveParams.templateId,
        }
        console.log('[miuta]构建创建请求参数', { createParams, styleSaveParams, enablePresetTemplates })
        await save(createParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        onCreateSuccess?.();
      } else {
        // 构建更新保存参数
        const updateParams = {
          uuid: triggerId,
          styleId: styleSaveParams.styleId,
          coverUrl: styleSaveParams.coverUrl,
          actorModelId,
          voiceModelId: styleSaveParams.voiceId || '',
          speedRatio: styleSaveParams.speedRatio,
          scene: styleSaveParams.scene,
        }
        console.log('[miuta]构建更新请求参数', { updateParams, styleSaveParams, enablePresetTemplates })
        await update(updateParams, {
          userId: account?.id || '',
          type: 1,
          agentId: params.id,
          autoRefresh: false
        });
        setTriggerId("");
        onCreateSuccess?.();
      }
    },
    onClose: async () => {
      setTriggerId('');
    }
  });

  // 统一的数据加载状态
  const isLoading = agentUserLoading || initialAppLoading;

  // 打开编辑模板
  const handleEdit = async (uuid?: string) => {
    setTriggerId(uuid || "");
    if (uuid) {
      // 获取模板详情
      const detail = await fetchDetail(uuid);
      // 取出数字人信息
      const actorInfo = agentUserActors?.find(item => item.modelId === detail.actorModelId)
      // 处理编辑参数
      const editIdParam = {
        coverUrl: detail.coverUrl,
        styleId: detail.styleModelId,
        digitalHumanInfo: {
          id: actorInfo?.id,
          name: detail.actorName,
          width: actorInfo?.width,
          height: actorInfo?.height,
          coverUrl: detail.actorPicUrl,
          renderUrl: detail.actorPicUrl,
        },
        voiceInfo: {
          id: detail.voiceModelId,
          name: detail.voiceName,
          coverUrl: detail.coverUrl,
          exampleUrl: detail.voiceSampleUrl,
          speedRatio: detail.speedRatio,
        }
      }
      openEditor({ editIdParam });
    }
  }

  useEffect(() => {
    // 当加载完成，且用户处于“等待新增”的状态时
    if (!isLoading && isPendingAdd) {
      setIsPendingAdd(false); // 重置状态

      if (enablePresetTemplates.length > 0) {
        openEditor({});
      } else {
        toast.toast({
          description: t("agent_creation.subscription_settings.no_preset_template"),
        });
      }
    }
  }, [isLoading, isPendingAdd, enablePresetTemplates, openEditor]);


  // 点击处理函数
  const handleAdd = () => {
    if (isLoading) {
      setIsPendingAdd(true);
      return;
    }
    if (enablePresetTemplates?.length > 0) {
      openEditor({});
    } else {
      toast.toast({
        description: t("agent_creation.subscription_settings.no_preset_template"),
      });
    }
  };

  // 删除模板
  const handleDelete = async (uuid: string) => {
    await deleteTemplate(uuid, account.id, {
      agentId: params.id,
      autoRefresh: false
    })
    onCreateSuccess?.()
  }

  useImperativeHandle(ref, () => ({
    onDelete: handleDelete,
    onEdit: handleEdit,
  }));

  useEffect(() => {
    onDetailLoadingChange?.(detailLoading);
  }, [detailLoading, onDetailLoadingChange]);

  return (
    <div className="w-full h-full flex flex-col relative">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[15px] text-[rgba(16,24,40,1)] font-medium">
          {t("agent_dashboard.user_detail.template.title")}
        </div>
        <button className="flex items-center gap-2" onClick={() => onOpenAll()}>
          <span className="text-sm text-[rgba(102,112,133,1)]">
            {t("agent_creation.subscription_settings.total_items", {
              totalItems: items.length,
            })}
          </span>
          <ChevronLeftThinIcon className="w-5 h-5 text-[rgba(152,162,179,1)]" />
        </button>
      </div>

      <div
        className="relative select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        {/* 左侧渐变遮罩 + 按钮 */}
        {canScrollLeft && (
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="absolute -left-5 top-1/2 -translate-y-1/2 h-full w-12 flex items-center justify-center z-10 bg-[linear-gradient(270deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_80%)]"
          >
            <div className="flex justify-center items-center -mt-4 w-6 h-6 bg-white rounded-full shadow-[0px_1.2px_2.4px_rgba(16,_24,_40,_0.05)]">
              <ChevronRightThinIcon className="w-4 h-4 text-i-sub" />
            </div>
          </button>
        )}

        {/* 4x2 网格分页内容 */}
        <div
          className="grid gap-x-3 gap-y-10"
          style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
        >
          {/* 新建(仅支持短视频) */}
          {showVideoAction && (
            <Tooltip
              side='top'
              align='center'
              trigger={
                <div
                  className={cn(
                    "relative mb-2.5 w-full aspect-[3/4] rounded-[8px] bg-[rgba(234,236,240,1)] cursor-pointer flex items-center justify-center ",
                    // agentUserLoading && "cursor-not-allowed pointer-events-none"
                  )}
                  onClick={handleAdd}
                >
                  <AddTemplateIcon className="text-[rgba(152,162,179,1)]" />
                  <LoadingOverlay loading={isPendingAdd} />
                </div>
              }
              content={
                <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                  <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                    {t("agent_creation.subscription_settings.add_template")}
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                </div>
              }
            />
          )}
          {loading ? (
            Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="w-full relative">
                <div className="mb-2.5 w-full aspect-[3/4] rounded-[8px] bg-gray-200 animate-pulse" />
                <div className="absolute -bottom-1 translate-y-4 h-4 bg-gray-200 rounded mx-auto w-2/3 animate-pulse translate-x-4" />
              </div>
            ))
          ) : (
            pageItems.length ? pageItems.map((tpl) => (
              <AgentTemplateVideoModal
                key={tpl.id}
                title={tpl.name}
                videoUrl={tpl.coverVideo}
                poster={tpl.coverUrl}
                trigger={
                  <button className="w-full relative group">
                    <div className="mb-2.5 w-full aspect-[3/4] rounded-[8px] overflow-hidden">
                      <Image
                        src={tpl.coverUrl}
                        className="size-full object-cover rounded-[8px] border border-[rgba(234,236,240,1)]"
                        alt={tpl.name}
                      />
                    </div>
                    <TruncatedText
                      className={cn(
                        "max-w-[90px] text-center text-sm text-[rgba(16,24,40,1)] font-normal",
                        "absolute -bottom-1 w-full translate-y-4"
                      )}
                      text={tpl.name}
                    />
                    {/* 更多操作 */}
                    {showVideoAction && !agentUserLoading && (
                      <div
                        className="opacity-0 group-hover:opacity-100 absolute top-1.5 right-1.5 transition-all z-20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreActionPopover
                          className="w-32"
                          side="bottom"
                          tiggerIcon={
                            <MoreIcon className="size-5 text-white" />
                          }
                          tiggerClassName="hover:bg-white/30 bg-[rgba(0,0,0,0.4)] shadow-sm"
                        >
                          <MoreActionItem
                            icon={<EditIcon className="w-4 h-4" />}
                            label={t("common.edit")}
                            className='text-[rgba(16,24,40,1)] font-medium'
                            onClick={() => handleEdit(tpl.uuid)}
                          />
                          <DeleteConfirmModal
                            trigger={
                              <MoreActionItem
                                icon={<TrashIcon className="w-4 h-4" />}
                                className='text-[rgba(16,24,40,1)] font-medium'
                                label={t("common.delete")}
                              />
                            }
                            loading={deleteLoading}
                            onConfirm={() => handleDelete(tpl.uuid)}
                          />
                        </MoreActionPopover>
                      </div>
                    )}
                    <LoadingOverlay loading={detailLoading && triggerId === tpl.uuid} />
                  </button>
                }
              />
            )) : (
              <div className=' w-full h-full relative  flex flex-col justify-center items-center col-span-full mt-3 mb-16 gap-2'>
                <TemplateEmptyIcon className='w-[120px] h-[103px]' />
                <span className='text-sm text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.template.template_empty2")}</span>
              </div>
            )
          )}
        </div>

        {/* 右侧渐变遮罩 + 按钮 */}
        {canScrollRight && (
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="absolute -right-5 top-1/2 -translate-y-1/2 h-full w-12 flex items-center justify-center z-10 bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_80%)]"
          >
            <div className="flex justify-center items-center -mt-4 w-6 h-6 bg-white rounded-full shadow-[0px_1.2px_2.4px_rgba(16,_24,_40,_0.05)]">
              <ChevronLeftThinIcon className="w-4 h-4 text-i-sub" />
            </div>
          </button>
        )}

        {/* 页码指示 */}
        <div className="mt-5 flex items-center justify-center gap-1">
          {totalPages > 1 &&
            Array.from({ length: totalPages }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === page
                  ? "bg-[rgba(16,24,40,1)]"
                  : "bg-[rgba(213,215,218,1)]"
                  }`}
              />
            ))}
        </div>
      </div>
      {createPortal(
        <div
          className='z-[999999]'
          style={{ pointerEvents: 'auto' }}
          // 阻止滚动事件冒泡到 document/body
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <StyleEditorMicroApp
            name="style-editor-microapp"
            env={env}
            onMicroAppMessage={handleMicroAppMessage}
            onMounted={handleMounted}
            onUnmounted={handleUnmounted}
          />
        </div>,
        document.body
      )}
    </div>
  );
});
